/**
 * Utilities for persisting and synchronizing the user's analytics consent state.
 *
 * The module keeps an in-memory representation of consent while also mirroring the
 * value to `localStorage` and the Google Analytics `gtag` consent API when those
 * capabilities are available in the current runtime environment.
 */

/** Storage key used to persist consent decisions in `localStorage`. */
const CONSENT_KEY = 'analytics-consent';

/**
 * Reads the previously persisted consent value from `localStorage`.
 *
 * @returns `true` when consent was granted, otherwise `false`.
 */
function getStoredConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored === 'granted';
  } catch {
    return false;
  }
}

/** Tracks the in-memory consent state for the current session. */
let consentGranted = getStoredConsent();

/** Listener invoked whenever the consent state changes. */
type ConsentListener = (granted: boolean) => void;

/** Active subscribers interested in consent transitions. */
const listeners = new Set<ConsentListener>();

function notifyListeners(granted: boolean) {
  listeners.forEach((listener) => {
    try {
      listener(granted);
    } catch {
      // ignore listener failures so others still fire
    }
  });
}

/**
 * Indicates whether analytics consent has been granted for the current session.
 *
 * @returns `true` when consent has been granted; otherwise `false`.
 */
export function isConsentGranted() {
  return consentGranted;
}

/**
 * Updates the user's consent choice and synchronizes it with persistent storage
 * and Google Analytics if available.
 *
 * @param granted - New consent value to record.
 */
export function setConsent(granted: boolean) {
  consentGranted = granted;

  // Persist to localStorage
  try {
    if (typeof window !== "undefined") {
      if (granted) {
        localStorage.setItem(CONSENT_KEY, 'granted');
      } else {
        localStorage.setItem(CONSENT_KEY, 'denied');
      }
    }
  } catch {
    // noop
  }

  // Update gtag consent
  try {
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("consent", "update", {
        analytics_storage: granted ? "granted" : "denied",
      });
    }
  } catch {
    // noop
  }
  notifyListeners(granted);
}

/**
 * Configures `gtag` with the default consent state.
 *
 * Consent is denied by default unless a persisted "granted" value exists. The
 * in-memory state is kept in sync with the stored value to ensure future checks
 * reflect the most up-to-date decision.
 */
export function initDefaultConsent() {
  try {
    const hasConsent = getStoredConsent();
    if (typeof window !== "undefined" && (window as any).gtag) {
      // Check if we already have consent from localStorage
      (window as any).gtag("consent", "default", {
        analytics_storage: hasConsent ? "granted" : "denied",
      });
    }
    // Update our internal state and propagate the default either way
    consentGranted = hasConsent;
    notifyListeners(consentGranted);
  } catch {
    // noop
  }
}

/**
 * Registers a callback that fires whenever the consent value changes.
 *
 * @param listener - Handler invoked with the latest consent state.
 * @returns Cleanup function to unsubscribe the listener.
 */
export function subscribeToConsentChanges(listener: ConsentListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
